import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ILiveChatConversation,
  ILiveChatConversationSummary,
  ILiveChatMessage,
  ILiveChatQueueStatus,
  LiveChatStatus,
  LiveChatTopic,
  normalizeConversation,
  normalizeMessage,
  normalizeSummary,
} from "../models/ILiveChat";
import {
  closeLiveChat,
  getActiveLiveChat,
  getLiveChatMessages,
  markLiveChatAsRead,
  rateLiveChat,
  requestLiveChatAgent,
  sendLiveChatAttachment,
  sendLiveChatMessage,
  startLiveChat,
} from "../services/liveChat";
import liveChatHub from "../services/liveChatHub";
import { useActiveOrder } from "../../../utils/ActiveOrderContext";
import { getUserToken } from "../../../utils/tokenUtils";
import { getApiErrorMessage } from "../../../utils/Util";

/** Navbar otomatik girişi tamamladığında yayınlanır; sayfa yenilenmeden oturum kurulur. */
export const AUTH_EVENT = "authChanged";

const MESSAGE_PAGE_SIZE = 30;

/** "Yazıyor" bilgisi kaybolan bir "durdu" olayında ekranda asılı kalmasın. */
const TYPING_TIMEOUT_MS = 4000;

interface OpenChatOptions {
  topic?: LiveChatTopic;
  orderId?: string | null;
  initialMessage?: string;
}

interface ILiveChatContext {
  isOpen: boolean;
  openChat: (options?: OpenChatOptions) => void;
  closePanel: () => void;

  conversation: ILiveChatConversation | null;
  messages: ILiveChatMessage[];
  queue: ILiveChatQueueStatus | null;
  unreadCount: number;
  counterpartTyping: boolean;

  loading: boolean;
  sending: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string;
  clearError: () => void;

  sendMessage: (content: string) => Promise<boolean>;
  sendAttachment: (file: File, caption?: string) => Promise<void>;
  requestAgent: (reason?: string) => Promise<void>;
  endConversation: () => Promise<void>;
  submitRating: (rating: number, comment?: string) => Promise<void>;
  loadOlderMessages: () => Promise<void>;
  notifyTyping: (isTyping: boolean) => void;
}

const LiveChatCtx = createContext<ILiveChatContext>({} as ILiveChatContext);

export const useLiveChat = () => useContext(LiveChatCtx);

const newClientMessageId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const LiveChatProvider: FC<PropsWithChildren> = ({ children }) => {
  // MainRout'ta ActiveOrderProvider bu sağlayıcının dışında olduğu için okunabilir.
  const { activeOrders } = useActiveOrder();
  const [isOpen, setIsOpen] = useState(false);
  const [conversation, setConversation] = useState<ILiveChatConversation | null>(null);
  const [messages, setMessages] = useState<ILiveChatMessage[]>([]);
  const [queue, setQueue] = useState<ILiveChatQueueStatus | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [counterpartTyping, setCounterpartTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");

  // Hub olay işleyicileri bir kez bağlanır; güncel değerlere ref üzerinden bakarlar.
  const conversationIdRef = useRef<string | null>(null);
  const conversationOrderIdRef = useRef<string | null>(null);
  const conversationStatusRef = useRef<LiveChatStatus | null>(null);
  const isOpenRef = useRef(false);
  const joinedConversationRef = useRef<string | null>(null);
  const oldestCursorRef = useRef<string | null>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    conversationIdRef.current = conversation?.id ?? null;
    conversationOrderIdRef.current = conversation?.orderId ?? null;
    conversationStatusRef.current = conversation?.status ?? null;
  }, [conversation]);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const clearError = useCallback(() => setError(""), []);

  /**
   * Tek mesaj birleştirme noktası.
   *
   * Aynı mesaj iki kez gelebilir: LiveChatNotifier bot/temsilci mesajını hem sohbet
   * grubuna hem müşterinin kişisel grubuna ayrı ayrı gönderir ve müşteri ikisinde de
   * bulunur. Kendi gönderdiğimiz mesaj da sohbet grubundan geri döner; onu iyimser
   * balonla eşleştirmek için clientMessageId kullanılır.
   */
  const upsertMessage = useCallback((incoming: ILiveChatMessage) => {
    setMessages((prev) => {
      const byId = prev.findIndex((m) => m.id === incoming.id);
      if (byId !== -1) {
        const next = [...prev];
        next[byId] = incoming;
        return next;
      }

      if (incoming.clientMessageId) {
        const byClientId = prev.findIndex(
          (m) => m.clientMessageId === incoming.clientMessageId
        );
        if (byClientId !== -1) {
          const next = [...prev];
          next[byClientId] = incoming;
          return next;
        }
      }

      return [...prev, incoming].sort(
        (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
      );
    });
  }, []);

  const markRead = useCallback((conversationId: string) => {
    markLiveChatAsRead(conversationId)
      .then(() => setUnreadCount(0))
      .catch(() => {});
  }, []);

  const applyConversation = useCallback(
    (raw: ILiveChatConversation) => {
      const normalized = normalizeConversation(raw);
      setConversation(normalized);
      setMessages(normalized.messages);
      setQueue(normalized.queue ?? null);
      setUnreadCount(isOpenRef.current ? 0 : normalized.unreadCount);

      // İlk sayfa dolu geldiyse daha eski mesaj olabilir.
      const oldest = normalized.messages[0];
      oldestCursorRef.current = oldest?.sentAt ?? null;
      setHasMore(normalized.messages.length >= MESSAGE_PAGE_SIZE);

      if (isOpenRef.current && normalized.unreadCount > 0) {
        markRead(normalized.id);
      }
      return normalized;
    },
    [markRead]
  );

  const resetState = useCallback(() => {
    setConversation(null);
    setMessages([]);
    setQueue(null);
    setUnreadCount(0);
    setCounterpartTyping(false);
    setHasMore(false);
    setError("");
    setIsOpen(false);
    conversationIdRef.current = null;
    joinedConversationRef.current = null;
    oldestCursorRef.current = null;
  }, []);

  /* ---------------------------------------------------------------- */
  /* Hub bağlantısı ve olayları                                        */
  /* ---------------------------------------------------------------- */

  const joinRoom = useCallback((conversationId: string) => {
    if (joinedConversationRef.current === conversationId) return;
    joinedConversationRef.current = conversationId;
    liveChatHub.joinConversation(conversationId);
  }, []);

  useEffect(() => {
    if (!conversation) return;
    if (!liveChatHub.isConnected) return;
    joinRoom(conversation.id);
  }, [conversation, joinRoom]);

  useEffect(() => {
    let cancelled = false;

    /**
     * Olay işleyicilerini bağlar. Her adımda önce off() çağrılır: bootstrap yeniden
     * çalıştığında (oturum yenilendiğinde) aynı olayın iki kez işlenmesini önler —
     * aksi halde okunmamış sayacı her mesajda iki artardı.
     */
    const bindHandlers = () => {
      liveChatHub.off("ReceiveMessage");
      liveChatHub.off("MessagesRead");
      liveChatHub.off("TypingChanged");
      liveChatHub.off("ConversationUpdated");
      liveChatHub.off("ConversationAssigned");
      liveChatHub.off("ConversationClosed");
      liveChatHub.off("QueueStatusChanged");

      liveChatHub.on<[ILiveChatMessage]>("ReceiveMessage", (raw) => {
        const message = normalizeMessage(raw);
        if (message.conversationId !== conversationIdRef.current) return;

        upsertMessage(message);

        if (message.senderRole !== "Customer") {
          setCounterpartTyping(false);
          if (isOpenRef.current) {
            markRead(message.conversationId);
          } else {
            setUnreadCount((n) => n + 1);
          }
        }
      });

      liveChatHub.on<[string, unknown, string]>("MessagesRead", (conversationId, readerRole) => {
        if (conversationId !== conversationIdRef.current) return;
        // Karşı taraf okuduğunda bizim gönderdiklerimiz çift tik olur.
        const readerIsCustomer = readerRole === "Customer" || readerRole === 0;
        if (readerIsCustomer) return;
        setMessages((prev) =>
          prev.map((m) => (m.senderRole === "Customer" ? { ...m, isRead: true } : m))
        );
      });

      liveChatHub.on<[string, unknown, string | null, boolean]>(
        "TypingChanged",
        (conversationId, senderRole, _senderName, isTyping) => {
          if (conversationId !== conversationIdRef.current) return;
          const fromCustomer = senderRole === "Customer" || senderRole === 0;
          if (fromCustomer) return;

          setCounterpartTyping(isTyping);
          if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
          if (isTyping) {
            typingTimerRef.current = setTimeout(
              () => setCounterpartTyping(false),
              TYPING_TIMEOUT_MS
            );
          }
        }
      );

      liveChatHub.on<[ILiveChatConversationSummary]>("ConversationUpdated", (raw) => {
        const summary = normalizeSummary(raw);
        if (summary.id !== conversationIdRef.current) return;
        setConversation((prev) =>
          prev
            ? { ...prev, status: summary.status, priority: summary.priority, topic: summary.topic }
            : prev
        );
        // Kuyruktan çıkıldıysa bekleme kartı kalkar.
        if (summary.status !== "WaitingForAgent") setQueue(null);
      });

      liveChatHub.on<[string, string, string | null]>(
        "ConversationAssigned",
        (conversationId, agentId, agentName) => {
          if (conversationId !== conversationIdRef.current) return;
          setConversation((prev) =>
            prev ? { ...prev, agentId, agentName, status: "Active", isBotHandled: false } : prev
          );
          setQueue(null);
        }
      );

      liveChatHub.on<[string, string | null]>("ConversationClosed", (conversationId) => {
        if (conversationId !== conversationIdRef.current) return;
        setConversation((prev) => (prev ? { ...prev, status: "Closed" } : prev));
        setQueue(null);
      });

      liveChatHub.on<[ILiveChatQueueStatus]>("QueueStatusChanged", (status) => {
        if (status.conversationId !== conversationIdRef.current) return;
        setQueue(status);
      });

      liveChatHub.setOnReconnected(() => {
        const id = conversationIdRef.current;
        joinedConversationRef.current = null;
        if (!id) return;
        joinRoom(id);
        // Bağlantı kopukken gelen mesajlar için transkripti tazele.
        getActiveLiveChat()
          .then((res) => applyConversation(res.data))
          .catch(() => {});
      });
    };

    const bootstrap = async () => {
      if (!getUserToken()) return;

      await liveChatHub.start();
      if (cancelled) return;

      bindHandlers();

      // Açık sohbet yoksa 404 döner — bu bir hata değil, "sohbet başlat" durumudur.
      try {
        const res = await getActiveLiveChat();
        if (!cancelled) {
          const active = applyConversation(res.data);
          joinRoom(active.id);
        }
      } catch {
        /* açık sohbet yok */
      }
    };

    bootstrap();

    const handleSessionExpired = () => {
      liveChatHub.stop();
      resetState();
    };
    const handleAuthChanged = () => {
      bootstrap();
    };

    window.addEventListener("session-expired", handleSessionExpired);
    window.addEventListener(AUTH_EVENT, handleAuthChanged);

    return () => {
      cancelled = true;
      window.removeEventListener("session-expired", handleSessionExpired);
      window.removeEventListener(AUTH_EVENT, handleAuthChanged);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      liveChatHub.off("ReceiveMessage");
      liveChatHub.off("MessagesRead");
      liveChatHub.off("TypingChanged");
      liveChatHub.off("ConversationUpdated");
      liveChatHub.off("ConversationAssigned");
      liveChatHub.off("ConversationClosed");
      liveChatHub.off("QueueStatusChanged");
      liveChatHub.stop();
    };
  }, [applyConversation, joinRoom, markRead, resetState, upsertMessage]);

  /* ---------------------------------------------------------------- */
  /* Eylemler                                                          */
  /* ---------------------------------------------------------------- */

  const openChat = useCallback(
    (options?: OpenChatOptions) => {
      if (!getUserToken()) return;

      setIsOpen(true);
      isOpenRef.current = true;
      setError("");

      // Asistan sipariş sorularını ancak sohbete sipariş bağlıysa yanıtlayabilir
      // (LiveChatBotService: "OrderStatus when order is not null"). Bağlı sipariş
      // yoksa "sipariş bilgilerinize erişemiyorum" deyip temsilciye devrediyor.
      // Bu yüzden çağıran bir sipariş vermediyse aktif siparişi kendimiz bağlıyoruz.
      const orderId =
        options?.orderId ??
        (activeOrders.length > 0 ? activeOrders[activeOrders.length - 1].id : null);

      // Kapanmış sohbet yeniden kullanılamaz; start yeni bir sohbet açar.
      const existing = conversationStatusRef.current === "Closed" ? null : conversationIdRef.current;
      const orderAlreadyAttached = conversationOrderIdRef.current === orderId;

      // Sipariş değişmediyse ve yeni bir mesaj yoksa start'a gitmeye gerek yok.
      if (existing && orderAlreadyAttached && !options?.initialMessage) {
        markRead(existing);
        return;
      }

      setLoading(true);
      liveChatHub
        .start()
        .then(() =>
          startLiveChat({
            topic: options?.topic,
            orderId,
            initialMessage: options?.initialMessage,
          })
        )
        .then((res) => {
          const active = applyConversation(res.data);
          joinRoom(active.id);
        })
        .catch((err) => setError(getApiErrorMessage(err, "Destek sohbeti açılamadı.")))
        .finally(() => setLoading(false));
    },
    [activeOrders, applyConversation, joinRoom, markRead]
  );

  const closePanel = useCallback(() => {
    setIsOpen(false);
    isOpenRef.current = false;
    setCounterpartTyping(false);
  }, []);

  /** Gönderim başarılıysa true; panel başarısızlıkta yazılan metni geri koyar. */
  const sendMessage = useCallback(
    async (content: string): Promise<boolean> => {
      const trimmed = content.trim();
      const conversationId = conversationIdRef.current;
      if (!trimmed || !conversationId) return false;

      const clientMessageId = newClientMessageId();
      const optimistic: ILiveChatMessage = {
        id: `pending-${clientMessageId}`,
        conversationId,
        // Panel balonun bize ait olduğunu kimlikten anlar; sunucu yanıtıyla
        // değiştirildiğinde balon taraf değiştirmesin.
        senderId: getUserToken()?.userId,
        senderRole: "Customer",
        messageType: "Text",
        content: trimmed,
        clientMessageId,
        sentAt: new Date().toISOString(),
        isRead: false,
      };

      upsertMessage(optimistic);
      setSending(true);
      setError("");

      try {
        const res = await sendLiveChatMessage({ conversationId, content: trimmed, clientMessageId });
        upsertMessage(normalizeMessage(res.data));
        return true;
      } catch (err) {
        // İyimser balonu geri al — metin panelde geri yazılır, kullanıcı kaybetmez.
        setMessages((prev) => prev.filter((m) => m.clientMessageId !== clientMessageId));
        setError(getApiErrorMessage(err, "Mesaj gönderilemedi."));
        return false;
      } finally {
        setSending(false);
      }
    },
    [upsertMessage]
  );

  const sendAttachment = useCallback(
    async (file: File, caption?: string) => {
      const conversationId = conversationIdRef.current;
      if (!conversationId) return;

      setSending(true);
      setError("");
      try {
        const res = await sendLiveChatAttachment({
          conversationId,
          file,
          caption,
          clientMessageId: newClientMessageId(),
        });
        upsertMessage(normalizeMessage(res.data));
        // Fotoğraf sohbeti her zaman temsilciye aktarır.
        setConversation((prev) => (prev ? { ...prev, isBotHandled: false } : prev));
      } catch (err) {
        setError(getApiErrorMessage(err, "Fotoğraf gönderilemedi."));
      } finally {
        setSending(false);
      }
    },
    [upsertMessage]
  );

  const requestAgent = useCallback(async (reason?: string) => {
    const conversationId = conversationIdRef.current;
    if (!conversationId) return;

    setError("");
    try {
      const res = await requestLiveChatAgent({ conversationId, reason });
      setQueue(res.data);
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              isBotHandled: false,
              // position 0 = anında bir temsilciye atandı.
              status: res.data.position === 0 ? "Active" : "WaitingForAgent",
            }
          : prev
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Temsilciye aktarılamadı."));
    }
  }, []);

  const endConversation = useCallback(async () => {
    const conversationId = conversationIdRef.current;
    if (!conversationId) return;

    setError("");
    try {
      await closeLiveChat({ conversationId });
      setConversation((prev) => (prev ? { ...prev, status: "Closed" } : prev));
      setQueue(null);
    } catch (err) {
      setError(getApiErrorMessage(err, "Sohbet kapatılamadı."));
    }
  }, []);

  const submitRating = useCallback(async (rating: number, comment?: string) => {
    const conversationId = conversationIdRef.current;
    if (!conversationId) return;

    setError("");
    try {
      await rateLiveChat({ conversationId, rating, comment });
      setConversation((prev) => (prev ? { ...prev, rating, ratingComment: comment ?? null } : prev));
    } catch (err) {
      setError(getApiErrorMessage(err, "Puan gönderilemedi."));
    }
  }, []);

  const loadOlderMessages = useCallback(async () => {
    const conversationId = conversationIdRef.current;
    const before = oldestCursorRef.current;
    if (!conversationId || !before || loadingMore) return;

    setLoadingMore(true);
    try {
      const res = await getLiveChatMessages(conversationId, before, MESSAGE_PAGE_SIZE);
      const older = res.data.messages.map(normalizeMessage);

      if (older.length > 0) {
        setMessages((prev) => {
          const existing = new Set(prev.map((m) => m.id));
          const merged = [...older.filter((m) => !existing.has(m.id)), ...prev];
          return merged.sort(
            (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
          );
        });
        oldestCursorRef.current = res.data.nextCursor ?? older[0].sentAt;
      }

      setHasMore(res.data.hasMore);
      if (!res.data.hasMore) oldestCursorRef.current = null;
    } catch (err) {
      setError(getApiErrorMessage(err, "Eski mesajlar yüklenemedi."));
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore]);

  const notifyTyping = useCallback((isTyping: boolean) => {
    const conversationId = conversationIdRef.current;
    if (!conversationId) return;
    liveChatHub.notifyTyping(conversationId, isTyping);
  }, []);

  return (
    <LiveChatCtx.Provider
      value={{
        isOpen,
        openChat,
        closePanel,
        conversation,
        messages,
        queue,
        unreadCount,
        counterpartTyping,
        loading,
        sending,
        loadingMore,
        hasMore,
        error,
        clearError,
        sendMessage,
        sendAttachment,
        requestAgent,
        endConversation,
        submitRating,
        loadOlderMessages,
        notifyTyping,
      }}
    >
      {children}
    </LiveChatCtx.Provider>
  );
};
